import clsx from "clsx";
import { FC, Ref, useRef } from "react";

import type { ToolCallContent } from "@tsmono/inspect-common/types";
import { ContentCode } from "@tsmono/react/components";
import { usePrismHighlight } from "@tsmono/react/hooks";

import { RenderedText } from "../../content/RenderedText";

import { kToolTodoContentType } from "./tool";
import { TodoWriteInput } from "./tool-input/TodoWriteInput";
import styles from "./ToolInput.module.css";

interface ToolInputProps {
  contentType?: string;
  contents?: unknown;
  toolCallView?: ToolCallContent;
  className?: string | string[];
}
export const ToolInput: FC<ToolInputProps> = (props) => {
  const { contentType, contents, toolCallView, className } = props;

  const sourceCodeRef = useRef<HTMLDivElement | null>(null);
  const useToolView = toolCallView && isValidView(toolCallView);

  const sourceCodeLength = useToolView
    ? toolCallView.content.length
    : contents
      ? typeof contents === "string"
        ? contents.length
        : JSON.stringify(contents).length
      : 0;
  usePrismHighlight(sourceCodeRef, sourceCodeLength || 0);

  if (!contents && !useToolView) return null;

  if (useToolView) {
    return (
      <RenderedText
        markdown={toolCallView.content || ""}
        ref={sourceCodeRef}
        className={clsx("tool-call-input", styles.toolView, className)}
      />
    );
  } else {
    return (
      <RenderTool
        contents={
          typeof contents === "string" ||
          (typeof contents === "object" && contents !== null)
            ? contents
            : ""
        }
        contentType={contentType || ""}
        parentRef={sourceCodeRef}
        className={className}
      />
    );
  }
};

interface RenderToolProps {
  contents: string | object;
  contentType: string;
  parentRef: Ref<HTMLDivElement>;
  className?: string | string[];
}

const RenderTool: FC<RenderToolProps> = ({
  contents,
  contentType,
  parentRef,
  className,
}) => {
  if (contentType === kToolTodoContentType) {
    return <TodoWriteInput contents={contents} parentRef={parentRef} />;
  }

  const formattedContent =
    typeof contents === "object" ? JSON.stringify(contents) : contents;

  return (
    <div ref={parentRef}>
      <pre className={clsx("tool-call-input", styles.outputPre, className)}>
        <ContentCode
          className={clsx(
            "source-code",
            "sourceCode",
            contentType ? `language-${contentType}` : undefined,
            styles.outputCode
          )}
          text={formattedContent}
        />
      </pre>
    </div>
  );
};

// Guard against invalid tool views (e.g., malformed bash tool content
// from older log files).
const isValidView = (view: ToolCallContent): boolean => {
  if (view.content === "```bash\nbash\n```\n") {
    return false;
  }
  return true;
};
